import { Fragment, useEffect, useRef, useState } from "react";
import {
    animate,
    motion,
    useInView,
    useMotionValueEvent,
    useReducedMotion,
    useScroll,
} from "framer-motion";
import "./Process.css";

const EASE = [0.2, 0.7, 0.2, 1];

const TICKS = Array.from({ length: 21 }, (_, i) => i);

const MARKERS = [
    { id: "call", label: "CALL", pos: 0 },
    { id: "demo", label: "DEMO", pos: 2 / 20 },
    { id: "payment", label: "PAYMENT", pos: 4 / 20 },
    { id: "brief", label: "BRIEF", pos: 8 / 20 },
    { id: "build", label: "BUILD", pos: 9 / 20 },
    { id: "live", label: "LIVE", pos: 1 },
];

const STEPS = [
    {
        numeral: "01",
        tag: "DAY 1",
        title: "Book a Call",
        copy: "We discuss goals, check whether we are the right fit, finalise the package.",
    },
    {
        numeral: "02",
        tag: "DAY 3",
        title: "Demo",
        copy: "See real edits, past results and the dashboard, so you know exactly what working with us looks like.",
    },
    {
        numeral: "03",
        tag: "DAY 5",
        title: "Payment Plans and Lock In",
        copy: "Pick a plan that fits your upload volume. Everything locked in writing before any work begins.",
    },
    {
        numeral: "04",
        tag: "DAY 9",
        title: "Creator Brief and Assets Transfer",
        copy: "Get your existing brand assets or create them. Onboard to our dashboard and systems. Get on the same page.",
    },
    {
        numeral: "05",
        tag: "DAYS 10–20",
        title: "Asset Creation and Building Stock",
        copy: "We build your content stockpile: hooks, caption styles, templates and a backlog of cuts ready to schedule.",
    },
    {
        numeral: "06",
        tag: "DAY 21",
        title: "Publishing Start",
        copy: "You record. We package, edit and publish. Monthly reports shared with you and weekly reports to test and fix internally.",
    },
];

const HEADLINE = [
    { text: "Up" },
    { text: "and" },
    { text: "running" },
    { text: "in" },
    { text: "three weeks.", accent: true },
];

const CYCLE_WORDS = ["RECORD", "PACKAGE", "EDIT", "PUBLISH", "REPORT"];

const DELIVERY = [
    { id: "short", label: "SHORT-FORM", first: 48, ready: 72 },
    { id: "long", label: "LONG-FORM", first: 72, ready: 96 },
];

const fade = (reduce, delay) => ({
    variants: {
        hidden: { opacity: 0, y: 18 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.7, delay, ease: EASE },
        },
    },
    initial: reduce ? false : "hidden",
    whileInView: "visible",
    viewport: { once: true, amount: 0.6 },
});

const headlineVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};

const wordVariants = {
    hidden: { opacity: 0, y: 26 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
};

const Counter = ({ value, reduce, testId }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
    const [display, setDisplay] = useState(reduce ? value : 0);

    useEffect(() => {
        if (reduce) {
            setDisplay(value);
            return undefined;
        }
        if (!inView) return undefined;
        const controls = animate(0, value, {
            duration: 1.6,
            ease: EASE,
            onUpdate: (v) => setDisplay(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, reduce, value]);

    return (
        <span className="process-delivery-number" ref={ref} data-testid={testId}>
            {display}h
        </span>
    );
};

const CycleRow = ({ reduce }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (reduce) return undefined;
        const id = setInterval(
            () => setIndex((i) => (i + 1) % CYCLE_WORDS.length),
            1200
        );
        return () => clearInterval(id);
    }, [reduce]);

    return (
        <p
            className="process-cycle"
            data-testid="process-cycle"
            aria-label="Record, package, edit, publish, report"
        >
            {CYCLE_WORDS.map((word, i) => (
                <Fragment key={word}>
                    <span
                        className={`process-cycle-word${i === index ? " is-lit" : ""}`}
                        aria-hidden="true"
                    >
                        {word}
                    </span>
                    {i < CYCLE_WORDS.length - 1 && (
                        <span className="process-cycle-arrow" aria-hidden="true">
                            →
                        </span>
                    )}
                </Fragment>
            ))}
        </p>
    );
};

export const Process = () => {
    const reduce = useReducedMotion();
    const sectionRef = useRef(null);
    const [activeStep, setActiveStep] = useState(0);
    const [activeTick, setActiveTick] = useState(0);
    const effectiveActive = reduce ? STEPS.length - 1 : activeStep;
    const effectiveTick = reduce ? TICKS.length - 1 : activeTick;

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start 0.9", "start 0.25"],
    });

    useMotionValueEvent(scrollYProgress, "change", (v) => {
        if (reduce) return;
        setActiveTick(Math.round(v * (TICKS.length - 1)));
        let idx = 0;
        MARKERS.forEach((m, i) => {
            if (v >= m.pos - 0.001) idx = i;
        });
        setActiveStep(idx);
    });

    const fillScale = reduce ? 1 : scrollYProgress;

    return (
        <section
            className="process"
            id="process"
            aria-labelledby="process-heading"
            data-testid="process-section"
            ref={sectionRef}
        >
            <div className="process-inner">
                <header className="process-header">
                    <div className="process-header-main">
                        <motion.p
                            className="process-eyebrow"
                            data-testid="process-eyebrow"
                            {...fade(reduce, 0)}
                        >
                            {"HOW IT WORKS"}
                        </motion.p>
                        <motion.h2
                            id="process-heading"
                            className="process-heading"
                            data-testid="process-heading"
                            variants={headlineVariants}
                            initial={reduce ? false : "hidden"}
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.6 }}
                        >
                            {HEADLINE.map((w, i) => (
                                <Fragment key={w.text}>
                                    <motion.span
                                        className={`process-heading-word${w.accent ? " process-heading-accent" : ""}`}
                                        variants={wordVariants}
                                    >
                                        {w.text}
                                    </motion.span>
                                    {i < HEADLINE.length - 1 ? " " : ""}
                                </Fragment>
                            ))}
                        </motion.h2>
                        <motion.p
                            className="process-sub"
                            data-testid="process-sub"
                            {...fade(reduce, 0.15)}
                        >
                            One call, one onboarding doc, then a weekly rhythm
                            you can set your calendar by.
                        </motion.p>
                    </div>
                    <motion.p
                        className="process-meta"
                        data-testid="process-meta"
                        {...fade(reduce, 0.2)}
                    >
                        {"/ ONBOARDING // 21 DAYS"}
                    </motion.p>
                </header>

                <div className="process-flow">
                    <div
                        className="process-timeline"
                        data-testid="process-timeline"
                        aria-hidden="true"
                    >
                        <div className="process-timeline-rail">
                            <motion.div
                                className="process-timeline-fill process-timeline-fill--x"
                                data-testid="process-timeline-fill"
                                style={{ scaleX: fillScale }}
                            />
                            <motion.div
                                className="process-timeline-fill process-timeline-fill--y"
                                style={{ scaleY: fillScale }}
                            />
                            {TICKS.map((i) => (
                                <span
                                    key={i}
                                    className={`process-tick${i <= effectiveTick ? " is-lit" : ""}`}
                                    style={{
                                        "--p": `${(i / (TICKS.length - 1)) * 100}%`,
                                    }}
                                >
                                    <span
                                        className={`process-tick-label${i === 0 ? " is-first" : ""}${i === TICKS.length - 1 ? " is-last" : ""}`}
                                    >
                                        D{String(i + 1).padStart(2, "0")}
                                    </span>
                                </span>
                            ))}
                            {MARKERS.map((m, i) => (
                                <span
                                    key={m.id}
                                    className={`process-marker${i <= effectiveActive ? " is-lit" : ""}${i === 0 ? " is-first" : ""}${i === MARKERS.length - 1 ? " is-last" : ""}`}
                                    style={{ "--p": `${m.pos * 100}%` }}
                                    data-testid={`process-marker-${m.id}`}
                                >
                                    <span className="process-marker-label">
                                        {m.label}
                                    </span>
                                    <span className="process-marker-dot" />
                                    <span className="process-marker-drop" />
                                </span>
                            ))}
                        </div>
                    </div>

                    <ol className="process-steps" data-testid="process-steps">
                        {STEPS.map((step, i) => (
                            <li
                                key={step.numeral}
                                className={`process-step${i <= effectiveActive ? " is-active" : ""}`}
                                data-testid={`process-step-${i + 1}`}
                            >
                                <span
                                    className="process-step-anchor"
                                    aria-hidden="true"
                                >
                                    <span className="process-step-anchor-dot" />
                                    <span className="process-step-anchor-label">
                                        {MARKERS[i].label}
                                    </span>
                                </span>
                                <span
                                    className="process-step-numeral"
                                    aria-hidden="true"
                                >
                                    {step.numeral}
                                </span>
                                <span className="process-step-tag">
                                    {step.tag}
                                </span>
                                <h3 className="process-step-title">
                                    {step.title}
                                </h3>
                                <p className="process-step-copy">{step.copy}</p>
                                {i === STEPS.length - 1 && (
                                    <CycleRow reduce={reduce} />
                                )}
                            </li>
                        ))}
                    </ol>
                </div>

                <div className="process-delivery" data-testid="process-delivery">
                    {DELIVERY.map((d) => {
                        const pct = (d.first / d.ready) * 100;
                        return (
                            <div
                                key={d.id}
                                className="process-delivery-half"
                                data-testid={`process-delivery-${d.id}`}
                            >
                                <span className="process-delivery-label">
                                    {d.label}
                                    <span
                                        className="process-cursor"
                                        aria-hidden="true"
                                    />
                                </span>
                                <div className="process-delivery-count">
                                    <Counter
                                        value={d.first}
                                        reduce={reduce}
                                        testId={`process-counter-${d.id}`}
                                    />
                                    <span className="process-delivery-firstcut">
                                        FIRST CUT
                                    </span>
                                </div>
                                <div className="process-delivery-rail-labels">
                                    <span
                                        className="process-rail-label process-rail-label--first"
                                        style={{ left: `${pct}%` }}
                                    >
                                        {d.first}H FIRST CUT
                                    </span>
                                </div>
                                <div
                                    className="process-delivery-rail"
                                    data-testid={`process-rail-${d.id}`}
                                >
                                    <span
                                        className="process-delivery-rail-dim"
                                        style={{
                                            left: `${pct}%`,
                                            width: `${100 - pct}%`,
                                        }}
                                    />
                                    <span
                                        className="process-delivery-rail-accent"
                                        style={{ width: `${pct}%` }}
                                    />
                                    <span
                                        className="process-delivery-rail-tick"
                                        style={{ left: `${pct}%` }}
                                    />
                                </div>
                                <div className="process-delivery-rail-footer">
                                    <span className="process-rail-label process-rail-label--ready">
                                        {d.ready}H READY TO PUBLISH
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
